import React, { useState } from 'react';
import { X, Shield, CheckCircle, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { submitLead } from '../services/apiService';

const UniversalPopupForm = ({ isOpen, onClose, title = "Protect Your Brand Today" }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await submitLead(formData);
      
      if (result.success) {
        setLoading(false);
        setSubmitted(true);
        
        // Show success message and redirect after 2 seconds
        setTimeout(() => {
          toast.success(result.message);
          window.location.href = `/application/main?customerid=${result.customerId}`;
        }, 2000);
      } else {
        toast.error(result.error);
        setLoading(false);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Popup Container - Form Only on Mobile, Two Column on Desktop */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden animate-slideUp flex flex-col md:flex-row">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Left Side - Marketing Content (Hidden on Mobile, Visible on Desktop) */}
        <div className="hidden md:flex md:w-5/12 bg-gradient-to-br from-brand-600 to-brand-700 p-10 text-white flex-col justify-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-semibold">CIPO Trademark Experts</span>
            </div>
            
            <h2 className="text-3xl font-bold mb-4 leading-tight">
              Start Your Legal Journey
            </h2>
            
            <p className="text-brand-100 text-sm leading-relaxed">
              Partner with Trademark Flare for expert CIPO trademark registration. Our specialists deliver comprehensive brand protection across Canada with a 99% success rate.
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1">99% Approval Rate</h3>
                <p className="text-brand-100 text-xs">Expert CIPO filing delivers exceptional results</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1">24/7 Customer Service</h3>
                <p className="text-brand-100 text-xs">Round-the-clock support for all your trademark needs</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1">500+ Brands Protected</h3>
                <p className="text-brand-100 text-xs">Trusted by Canadian businesses nationwide</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form (Full Width on Mobile, 7/12 on Desktop) */}
        <div className="w-full md:w-7/12 p-4 md:p-10">
          {!submitted ? (
            <>
              <h2 className="text-lg md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{title}</h2>
              <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-6">Fill out the form and our experts will respond within 24 hours</p>

              <form onSubmit={handleSubmit} className="space-y-2 md:space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-9 md:pl-11 pr-3 py-2 md:pr-4 md:py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 md:gap-3">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      className="w-20 md:w-auto px-2 py-2 md:px-3 md:py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm"
                    >
                      <option value="+1">+1 US/Canada</option>
                      <option value="+44">+44 UK</option>
                      <option value="+91">+91 India</option>
                      <option value="+86">+86 China</option>
                      <option value="+81">+81 Japan</option>
                      <option value="+82">+82 South Korea</option>
                      <option value="+61">+61 Australia</option>
                      <option value="+49">+49 Germany</option>
                      <option value="+33">+33 France</option>
                      <option value="+39">+39 Italy</option>
                      <option value="+34">+34 Spain</option>
                      <option value="+7">+7 Russia</option>
                      <option value="+55">+55 Brazil</option>
                      <option value="+52">+52 Mexico</option>
                      <option value="+27">+27 South Africa</option>
                      <option value="+971">+971 UAE</option>
                      <option value="+65">+65 Singapore</option>
                      <option value="+31">+31 Netherlands</option>
                      <option value="+46">+46 Sweden</option>
                    </select>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setFormData({ ...formData, phone: value });
                        }
                      }}
                      className="flex-1 px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none text-sm"
                      placeholder="1234567890"
                      maxLength="10"
                    />
                  </div>
                </div>

                {/* Your Message */}
                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                    Your Message <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows="3"
                      className="w-full pl-9 md:pl-11 pr-3 py-2 md:pr-4 md:py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none resize-none text-sm"
                      placeholder="Tell us about your legal needs..."
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold py-3 md:py-4 px-6 md:px-8 rounded-lg hover:from-brand-700 hover:to-brand-800 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm md:text-base"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-1">Your request has been received successfully!</p>
              <p className="text-sm text-gray-500">Our experts will contact you within 24 hours</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UniversalPopupForm;
